import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "../../utils/classNames";


interface TooltipProps {
  content: string;
  children: ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

export default function Tooltip({
  content,
  children,
  position = "top",
  className
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const arrowClasses = {
    top: "top-full left-1/2 -translate-x-1/2 border-t-gray-900 border-l-transparent border-r-transparent border-b-transparent",
    bottom: "bottom-full left-1/2 -translate-x-1/2 border-b-gray-900 border-l-transparent border-r-transparent border-t-transparent",
    left: "left-full top-1/2 -translate-y-1/2 border-l-gray-900 border-t-transparent border-b-transparent border-r-transparent",
    right: "right-full top-1/2 -translate-y-1/2 border-r-gray-900 border-t-transparent border-b-transparent border-l-transparent",
  };

  const computePosition = () => {
    const trigger = wrapperRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;

    const t = trigger.getBoundingClientRect();
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const gap = 8;

    let top = 0;
    let left = 0;
    switch (position) {
      case "bottom":
        top = t.bottom + gap;
        left = t.left + t.width / 2 - w / 2;
        break;
      case "left":
        top = t.top + t.height / 2 - h / 2;
        left = t.left - w - gap;
        break;
      case "right":
        top = t.top + t.height / 2 - h / 2;
        left = t.right + gap;
        break;
      case "top":
      default:
        top = t.top - h - gap;
        left = t.left + t.width / 2 - w / 2;
        break;
    }
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!shouldRender) return;
    computePosition();
    // Recompute if layout shifts while shown (scroll/resize)
    const onUpdate = () => computePosition();
    window.addEventListener("scroll", onUpdate, true);
    window.addEventListener("resize", onUpdate);
    return () => {
      window.removeEventListener("scroll", onUpdate, true);
      window.removeEventListener("resize", onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, position, content]);

  const handleMouseEnter = () => {
    setShouldRender(true);
    setTimeout(() => setIsVisible(true), 10);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldRender(false);
      setCoords(null);
    }, 200);
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames("relative inline-block", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {shouldRender && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: "fixed",
              top: coords?.top ?? -9999,
              left: coords?.left ?? -9999,
            }}
            className={classNames(
              "z-50 px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg whitespace-nowrap pointer-events-none transition-[opacity,transform] duration-200",
              isVisible && coords ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
          >
            {content}
            <div
              className={classNames(
                "absolute w-0 h-0 border-4",
                arrowClasses[position]
              )}
            />
          </div>,
          document.body
        )}
    </div>
  );
}

